/**
 * The portal frontend's calls to its two backends, on top of the generated
 * clients (see src/lib/api-client.ts):
 *
 *  - the claims-portal API (src/client/portal), and
 *  - each partner's claims-server API (src/client/claims), addressed by the
 *    `apiBaseUrl` of the Partner from /listPartners.
 *
 * Request and response types come from the OpenAPI specs; re-export the ones
 * the views need from the generated modules rather than redeclaring them.
 */
import {
  getInfo,
  getSearchFreeText,
  postClaimcaseByKeyByClaimcaseKeyClaimPaymentMarkAsFulfilled,
  postClaimcaseByKeyByClaimcaseKeyClaimPaymentRemove,
  postClaimcaseByKeyByClaimcaseKeyClaimPaymentSignPayout,
} from '../client/claims/sdk.gen';
import type {
  ClaimKey,
  ClaimcaseKey,
  Info,
  PaymentKey,
  SearchResult,
  TransactionDay,
} from '../client/claims/types.gen';
import {
  getAssignmentsGetFromOpenClaimcases,
  getAutoCreateEmailTemplatesList,
  getEmailTemplatesList,
  getListPartners,
  getPaymentsGetFromOpenClaimcases,
  getSebHealth,
  postAutoCreateEmailTemplatesCopy,
  postAutoCreateEmailTemplatesSync,
  postEmailTemplatesCopy,
  postEmailTemplatesSync,
  postExportPayments,
  postSyncSebReports,
} from '../client/portal/sdk.gen';
import type {
  AnyClaimPaymentOut,
  AssignmentsResponse,
  AutoCreateEmailTemplateKey,
  AutoCreateEmailTemplateStatus,
  EmailTemplateKey,
  EmailTemplateStatus,
  Partner,
  PartnerName,
  PaymentExportInfo,
  SebHealth,
  SyncInfo,
} from '../client/portal/types.gen';
import { partnerApi } from '../lib/api-client';
import { sdk } from '../lib/sdk';

// ---------------------------------------------------------------------------
// Portal API
// ---------------------------------------------------------------------------

export const getPartnersService = (): Promise<Partner[]> =>
  sdk('Could not fetch partners', () => getListPartners());

export const getPaymentsFromOpenClaimCasesService = (): Promise<
  AnyClaimPaymentOut[]
> =>
  sdk('Could not fetch payments', () => getPaymentsGetFromOpenClaimcases());

export const getAssignmentsFromOpenClaimCasesService =
  (): Promise<AssignmentsResponse> =>
    sdk('Could not fetch assignments', () =>
      getAssignmentsGetFromOpenClaimcases()
    );

export const exportPaymentsService = (
  paymentKeys: PaymentKey[]
): Promise<PaymentExportInfo[]> =>
  sdk('Could not export payments', () =>
    postExportPayments({ body: paymentKeys })
  );

export const syncPaymentsWithSebService = (): Promise<SyncInfo[]> =>
  sdk('Could not sync payments with SEB', () => postSyncSebReports());

export const getSebHealthService = (): Promise<SebHealth> =>
  sdk('Could not fetch SEB integration status', () => getSebHealth());

export const getEmailTemplates = (): Promise<EmailTemplateStatus[]> =>
  sdk('Could not fetch e-mail templates', () => getEmailTemplatesList());

export const copyEmailTemplate = async (
  partnerName: PartnerName,
  templateKeys: EmailTemplateKey[]
): Promise<void> => {
  await sdk('Could not copy e-mail templates', () =>
    postEmailTemplatesCopy({ body: { partnerName, templateKeys } })
  );
};

export const syncEmailTemplates = async (): Promise<void> => {
  await sdk('Could not sync e-mail templates', () => postEmailTemplatesSync());
};

export const getAutoCreateEmailTemplates = (): Promise<
  AutoCreateEmailTemplateStatus[]
> =>
  sdk('Could not fetch auto-create e-mail templates', () =>
    getAutoCreateEmailTemplatesList()
  );

export const copyAutoCreateEmailTemplate = async (
  partnerName: PartnerName,
  templateKeys: AutoCreateEmailTemplateKey[]
): Promise<void> => {
  await sdk('Could not copy auto-create e-mail templates', () =>
    postAutoCreateEmailTemplatesCopy({ body: { partnerName, templateKeys } })
  );
};

export const syncAutoCreateEmailTemplates = async (): Promise<void> => {
  await sdk('Could not sync auto-create e-mail templates', () =>
    postAutoCreateEmailTemplatesSync()
  );
};

// ---------------------------------------------------------------------------
// Partner claims APIs -- every call names the partner by its apiBaseUrl
// ---------------------------------------------------------------------------

export const getPartnerInfoService = (partnerApiUrl: string): Promise<Info> =>
  sdk('Could not fetch partner info', () =>
    getInfo({ ...partnerApi(partnerApiUrl) })
  );

export const searchService = (
  partnerApiUrl: string,
  text: string
): Promise<SearchResult[]> =>
  sdk(`Something went wrong when trying to search for "${text.trim()}"`, () =>
    getSearchFreeText({ ...partnerApi(partnerApiUrl), query: { text } })
  );

export const signPaymentService = async (
  partnerApiUrl: string,
  claimcaseKey: ClaimcaseKey,
  claimKey: ClaimKey,
  paymentKey: PaymentKey
): Promise<void> => {
  await sdk('Could not sign payment', () =>
    postClaimcaseByKeyByClaimcaseKeyClaimPaymentSignPayout({
      ...partnerApi(partnerApiUrl),
      path: { claimcaseKey },
      body: { claimKey, paymentKey },
    })
  );
};

export const markPaymentAsFulfilledService = async (
  partnerApiUrl: string,
  claimcaseKey: ClaimcaseKey,
  claimKey: ClaimKey,
  paymentKey: PaymentKey,
  transactionDay: TransactionDay
): Promise<void> => {
  await sdk('Could not mark payment as fulfilled', () =>
    postClaimcaseByKeyByClaimcaseKeyClaimPaymentMarkAsFulfilled({
      ...partnerApi(partnerApiUrl),
      path: { claimcaseKey },
      body: { claimKey, paymentKey, transactionDay },
    })
  );
};

export const removePaymentService = async (
  partnerApiUrl: string,
  claimcaseKey: ClaimcaseKey,
  claimKey: ClaimKey,
  paymentKey: PaymentKey
): Promise<void> => {
  await sdk('Could not remove payment', () =>
    postClaimcaseByKeyByClaimcaseKeyClaimPaymentRemove({
      ...partnerApi(partnerApiUrl),
      path: { claimcaseKey },
      body: { claimKey, paymentKey },
    })
  );
};
